import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useT } from '../../i18n';
import { useGetHistory } from './context/PredictionsContext';
import { FaArrowUp, FaArrowDown, FaClock, FaCheckCircle, FaExclamationTriangle, FaEye, FaEyeSlash, FaFileDownload, FaSync } from 'react-icons/fa';
import { useBullvsBearContract } from './hooks/useBullvsBearContract';
import { useAccount, usePublicClient } from 'wagmi';
import { formatUnits } from 'viem';
import { bullvsbear } from '../../.config';
import BullvsBearABI from '../../abi/bullvsbearABI.json';
import UserStatsPanel from './components/UserStatsPanel';
import HistoryPagination from './components/HistoryPagination';
import ExportDataButton from './components/ExportDataButton';
import StatsFilters from './components/StatsFilters';

/* global BigInt */

const History = () => {
  const t = useT();
  const contextHistory = useGetHistory(); // Dados do contexto
  const [contractHistory, setContractHistory] = useState([]); // Dados diretos do contrato
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const { claimWinnings } = useBullvsBearContract();
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [lastUpdate, setLastUpdate] = useState(0);
  const [statsFilters, setStatsFilters] = useState({
    timeRange: 'all',
    position: 'all',
    outcome: 'all'
  });

  const { address } = useAccount();
  const publicClient = usePublicClient();


  const fetchSingleRound = async (epoch) => {
    try {
      const currentEpochNum = await publicClient.readContract({
        address: bullvsbear,
        abi: BullvsBearABI,
        functionName: 'currentEpoch'
      });

      // CORREÇÃO: Pular round atual (live) e próximo round
      const currentEpoch = Number(currentEpochNum) - 1;
      console.log(`Current epoch: ${currentEpoch}, fetching data for epoch: ${epoch}`);
      if (epoch >= currentEpoch) {
        console.log(`Skipping epoch ${epoch} - is current/future round`);
        return null;
      }

      const [userBetData, roundData] = await Promise.all([
        publicClient.readContract({
          address: bullvsbear,
          abi: BullvsBearABI,
          functionName: 'ledger',
          args: [BigInt(epoch), address]
        }),
        publicClient.readContract({
          address: bullvsbear,
          abi: BullvsBearABI,
          functionName: 'rounds',
          args: [BigInt(epoch)]
        })
      ]);

      if (userBetData && userBetData[1] && userBetData[1] > 0n) {
        const userPosition = userBetData[0] === 0 ? 'up' : 'down';
        const userAmount = Number(formatUnits(userBetData[1], 6));
        const userClaimed = userBetData[2];

        // CORREÇÃO: Índices corretos do contrato
        const lockPrice = Number(formatUnits(roundData[5], 8));
        const closePrice = Number(formatUnits(roundData[6], 8));
        const totalAmount = Number(formatUnits(roundData[7], 6));
        const bullAmount = Number(formatUnits(roundData[8], 6));
        const bearAmount = Number(formatUnits(roundData[9], 6));
        const rewardBaseCalAmount = Number(formatUnits(roundData[10] || 0n, 6));
        const rewardAmount = Number(formatUnits(roundData[11] || 0n, 6));
        const oracleCalled = roundData[12];
        const timestamp = Number(roundData[2]) * 1000;

        let userWon = false;
        let roundOutcome = 'error';
        let payout = 0;

        // Debug específico para round #11
        if (epoch === 11) {
          console.log('=== DEBUG ROUND #11 ===');
          console.log('lockPrice:', lockPrice);
          console.log('closePrice:', closePrice);
          console.log('rewardBaseCalAmount:', rewardBaseCalAmount);
          console.log('rewardAmount:', rewardAmount);
          console.log('totalAmount:', totalAmount);
          console.log('bullAmount:', bullAmount);
          console.log('bearAmount:', bearAmount);
          console.log('userAmount:', userAmount);
          console.log('userPosition:', userPosition);
        }

        if (!isNaN(lockPrice) && !isNaN(closePrice) && oracleCalled) {
          if (closePrice > lockPrice) {
            // Bull wins
            roundOutcome = 'up';
            userWon = userPosition === 'up';
            if (userWon && rewardAmount > 0 && rewardBaseCalAmount > 0) {
              // CORREÇÃO: Use contract logic like RoundCard
              payout = (userAmount / rewardBaseCalAmount) * rewardAmount;
            } else if (userWon && bullAmount > 0 && rewardAmount > 0) {
              // Fallback if rewardBaseCalAmount not available
              payout = (userAmount / bullAmount) * rewardAmount;
            }
          } else if (closePrice < lockPrice) {
            // Bear wins
            roundOutcome = 'down';
            userWon = userPosition === 'down';
            if (userWon && rewardAmount > 0 && rewardBaseCalAmount > 0) {
              // CORREÇÃO: Use contract logic like RoundCard
              payout = (userAmount / rewardBaseCalAmount) * rewardAmount;
            } else if (userWon && bearAmount > 0 && rewardAmount > 0) {
              // Fallback if rewardBaseCalAmount not available
              payout = (userAmount / bearAmount) * rewardAmount;
            }
          } else {
            // TIE - CORREÇÃO: Use same logic as RoundCard
            const totalBetAmount = bullAmount + bearAmount;
            const isDelayedRefund = rewardBaseCalAmount > 0 && Math.abs(rewardBaseCalAmount - totalBetAmount) < 0.01;

            if (isDelayedRefund && rewardAmount > 0) {
              // Tie com refund (delayed ou tie especial)
              roundOutcome = 'tie_refund';
              userWon = true;
              payout = (userAmount / rewardBaseCalAmount) * rewardAmount;
            } else if (rewardAmount > 0 && rewardBaseCalAmount === 0) {
              // Regular tie with refund
              roundOutcome = 'tie_refund';
              userWon = true;
              const winningPool = userPosition === 'up' ? bullAmount : bearAmount;
              payout = (userAmount / winningPool) * rewardAmount;
            } else {
              // Tie normal - casa ganha
              roundOutcome = 'tie_lose';
              userWon = false;
              payout = 0;
            }
          }
        }

        // Debug final para round #11
        if (epoch === 11) {
          console.log('Final result:');
          console.log('roundOutcome:', roundOutcome);
          console.log('userWon:', userWon);
          console.log('payout:', payout);
        }

        return {
          epoch: epoch.toString(),
          position: userPosition,
          amount: userAmount.toFixed(2),
          won: userWon,
          claimed: userClaimed,
          payout: payout.toFixed(2),
          lockPrice: lockPrice.toFixed(2),
          closePrice: closePrice.toFixed(2),
          roundOutcome,
          timestamp,
          rewardBaseCalAmount: rewardBaseCalAmount.toFixed(2),
          rewardAmount: rewardAmount.toFixed(2),
          totalBetAmount: (bullAmount + bearAmount).toFixed(2)
        };
      }
      return null;
    } catch (error) {
      console.error(`Error fetching data for epoch ${epoch}:`, error);
      return null;
    }
  };

  // Função para buscar lotes de rodadas
  const fetchRoundBatch = useCallback(async (startEpoch, batchSize, append = false) => {
    if (!address || !publicClient) return;

    try {
      setLoadingMore(true);
      console.log(`Fetching batch: epochs ${Math.max(1, startEpoch - batchSize + 1)} to ${startEpoch}`);

      const allRounds = [];
      const endEpoch = Math.max(1, startEpoch - batchSize + 1);

      // Criar array de promessas para buscar em paralelo (com limite)
      const batchPromises = [];

      for (let epoch = startEpoch; epoch >= endEpoch; epoch--) {
        batchPromises.push(fetchSingleRound(epoch));

        // Processar em lotes menores para evitar sobrecarga
        if (batchPromises.length >= 10 || epoch === endEpoch) {
          const results = await Promise.allSettled(batchPromises);
          results.forEach(result => {
            if (result.status === 'fulfilled' && result.value) {
              allRounds.push(result.value);
            }
          });
          batchPromises.length = 0; // Limpar array
        }
      }

      console.log(`Batch fetch complete, found ${allRounds.length} rounds with bets`);

      // Atualizar histórico (append ou replace)
      if (append) {
        setContractHistory(prev => [...prev, ...allRounds]);
      } else {
        setContractHistory(allRounds);
      }

      // Se não encontrou nada e ainda há mais rodadas para buscar, continuar buscando
      if (allRounds.length === 0 && endEpoch > 1) {
        await fetchRoundBatch(endEpoch - 1, batchSize, append);
      }

      return allRounds;
    } catch (error) {
      console.error('Error fetching round batch:', error);
      return [];
    } finally {
      setLoadingMore(false);
    }
  }, [address, publicClient]);

  // Buscar histórico diretamente do contrato com suporte a carregamento incremental
  const fetchHistoryFromContract = useCallback(async (forceRefresh = false) => {
    if (!address || !publicClient) {
      setLoading(false);
      return;
    }

    // Rate limiting - só atualizar a cada 10 segundos
    const now = Date.now();
    if (!forceRefresh && now - lastUpdate < 10000) {
      console.log('Skipping fetch - too soon since last update');
      return;
    }

    setLoading(true);
    setLastUpdate(now);

    try {
      console.log('Fetching history directly from contract...');

      const currentEpochNum = await publicClient.readContract({
        address: bullvsbear,
        abi: BullvsBearABI,
        functionName: 'currentEpoch'
      });

      // Iniciar com as rodadas mais recentes
      await fetchRoundBatch(Number(currentEpochNum), 50);
    } catch (error) {
      console.error('Error fetching history from contract:', error);
      setContractHistory([]);
    } finally {
      setLoading(false);
    }
  }, [address, publicClient, lastUpdate, fetchRoundBatch]);

  // Função para carregar mais histórico (rodadas mais antigas)
  const loadMoreHistory = useCallback(async () => {
    if (contractHistory.length === 0 || loadingMore) return;

    // Encontrar a rodada mais antiga já carregada
    const oldestEpoch = Math.min(...contractHistory.map(entry => parseInt(entry.epoch)));

    // Buscar lote anterior
    if (oldestEpoch > 1) {
      await fetchRoundBatch(oldestEpoch - 1, 50, true);
    }
  }, [contractHistory, loadingMore, fetchRoundBatch]);

  // Escutar eventos de atualização
  useEffect(() => {
    const handleRoundClaimed = (event) => {
      const { epoch } = event.detail;
      console.log(`Round ${epoch} claimed, updating history...`);

      // Atualizar contractHistory
      setContractHistory(prev => prev.map(entry => {
        if (entry.epoch === epoch) {
          return { ...entry, claimed: true };
        }
        return entry;
      }));
    };

    const handleNewBet = () => {
      console.log('New bet detected, scheduling history refresh...');
      setTimeout(() => {
        fetchHistoryFromContract(true); // Force refresh
      }, 3000);
    };

    const handleHistoryUpdate = () => {
      console.log('Manual history update requested...');
      fetchHistoryFromContract(true);
    };

    window.addEventListener('roundClaimed', handleRoundClaimed);
    window.addEventListener('newBetPlaced', handleNewBet);
    window.addEventListener('forceHistoryRefresh', handleHistoryUpdate);
    window.addEventListener('roundRefunded', handleRoundClaimed);

    return () => {
      window.removeEventListener('roundClaimed', handleRoundClaimed);
      window.removeEventListener('newBetPlaced', handleNewBet);
      window.removeEventListener('forceHistoryRefresh', handleHistoryUpdate);
      window.removeEventListener('roundRefunded', handleRoundClaimed);
    };
  }, [fetchHistoryFromContract]);

  // Buscar histórico inicial
  useEffect(() => {
    fetchHistoryFromContract();
  }, [fetchHistoryFromContract]);

  // Usar dados do contrato se disponíveis, senão usar contexto
  const history = contractHistory.length > 0 ? contractHistory : contextHistory;

  // Filtrar histórico com base nos filtros de estatísticas
  const filteredHistory = useMemo(() => {
    if (!history || history.length === 0) return [];

    return history.filter(entry => {
      // Filtrar por período
      if (statsFilters.timeRange !== 'all') {
        const entryDate = new Date(entry.timestamp || Date.now());
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        if (statsFilters.timeRange === 'today' && entryDate < today) return false;
        if (statsFilters.timeRange === 'yesterday' && (entryDate < yesterday || entryDate >= today)) return false;
        if (statsFilters.timeRange === 'week' && entryDate < weekStart) return false;
        if (statsFilters.timeRange === 'month' && entryDate < monthStart) return false;
      }

      // Filtrar por posição
      if (statsFilters.position !== 'all' && entry.position !== statsFilters.position) return false;

      // Filtrar por resultado
      if (statsFilters.outcome === 'won' && !entry.won) return false;
      if (statsFilters.outcome === 'lost' && entry.won) return false;

      return true;
    });
  }, [history, statsFilters]);

  // Aplicar filtros adicionais (pendentes, ganhos, perdas)
  const processedHistory = useMemo(() => {
    if (!filteredHistory || filteredHistory.length === 0) return [];

    // Filtrar com base no filtro selecionado
    let filtered = [...filteredHistory];

    if (filter === 'won') {
      filtered = filtered.filter(entry => entry.won);
    } else if (filter === 'lost') {
      filtered = filtered.filter(entry => !entry.won);
    } else if (filter === 'pending') {
      filtered = filtered.filter(entry => entry.won && !entry.claimed);
    }

    // Ordenar por epoch (mais recente primeiro)
    filtered.sort((a, b) => {
      const epochA = parseInt(a.epoch);
      const epochB = parseInt(b.epoch);
      return epochB - epochA;
    });

    return filtered;
  }, [filteredHistory, filter]);

  // Aplicar paginação
  const paginatedHistory = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return processedHistory.slice(startIndex, startIndex + itemsPerPage);
  }, [processedHistory, currentPage, itemsPerPage]);

  // Estatísticas do histórico
  const totalEntries = history.length;
  const filteredEntries = filteredHistory.length;
  const processedEntries = processedHistory.length;
  const pendingClaimsCount = history.filter(entry => entry.won && !entry.claimed).length;

  // Manipuladores de eventos
  const handleClaim = async (epoch) => {
    try {
      const result = await claimWinnings(epoch);
      if (result.success) {
        console.log('Claim successful, history will be updated automatically');

        // Atualizar imediatamente o estado local
        setContractHistory(prev => prev.map(entry => {
          if (entry.epoch === epoch) {
            return { ...entry, claimed: true };
          }
          return entry;
        }));
      }
    } catch (error) {
      console.error('Error claiming winnings:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setStatsFilters(newFilters);
    setCurrentPage(1); // Resetar para primeira página quando filtros mudam
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0); // Scroll para o topo quando mudar de página
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Resetar para primeira página
  };

  const handleManualRefresh = () => {
    const now = Date.now();
    if (now - lastUpdate < 5000) {
      console.log('Please wait before refreshing again');
      return;
    }
    setLoading(true);
    fetchHistoryFromContract(true);
  };

  if (loading && history.length === 0) {
    return (
      <div className="min-h-screen w-full bg-[#0d0d0d] flex flex-col items-center justify-center">
        <div className="text-center">
          <FaClock className="text-4xl text-gray-600 mb-2 spin" />
          <p className="text-gray-400">{t('bvb.loadingHistory')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#0d0d0d] flex flex-col">
      {/* Header fixo */}
      <div className="sticky top-0 z-10 bg-[#0d0d0d] border-b border-brand/30 p-4 space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">{t('bvb.history')}</h2>
          <div className="flex items-center gap-2">
            {pendingClaimsCount > 0 && (
              <div className="flex items-center gap-1 bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full text-xs font-medium">
                <FaExclamationTriangle size={10} />
                {pendingClaimsCount} pending claim{pendingClaimsCount > 1 ? 's' : ''}
              </div>
            )}

            {/* Botão de exportação */}
            <ExportDataButton
              history={filteredHistory}
              stats={{
                totalBets: filteredEntries,
                totalWins: filteredHistory.filter(e => e.won).length,
                totalLosses: filteredHistory.filter(e => !e.won).length,
                winRate: filteredEntries > 0 ? ((filteredHistory.filter(e => e.won).length / filteredEntries) * 100).toFixed(1) : '0.0',
                totalBetAmount: filteredHistory.reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2),
                totalWinAmount: filteredHistory.filter(e => e.won).reduce((sum, e) => sum + parseFloat(e.payout), 0).toFixed(2),
                profitLoss: (filteredHistory.filter(e => e.won).reduce((sum, e) => sum + parseFloat(e.payout), 0) -
                  filteredHistory.reduce((sum, e) => sum + parseFloat(e.amount), 0)).toFixed(2),
                profitPercentage: filteredHistory.reduce((sum, e) => sum + parseFloat(e.amount), 0) > 0 ?
                  ((filteredHistory.filter(e => e.won).reduce((sum, e) => sum + parseFloat(e.payout), 0) -
                    filteredHistory.reduce((sum, e) => sum + parseFloat(e.amount), 0)) /
                    filteredHistory.reduce((sum, e) => sum + parseFloat(e.amount), 0) * 100).toFixed(1) : '0.0'
              }}
              filters={statsFilters}
            />

            {/* Botão de refresh manual */}
            <button
              onClick={handleManualRefresh}
              disabled={loading}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${loading ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed' : 'bg-brand/20 text-brand hover:bg-brand/30'
                }`}
            >
              <FaSync className={loading ? 'spin' : ''} size={12} />
              {loading ? t('bvb.loading') : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Filtros de estatísticas */}
        <StatsFilters onFilterChange={handleFilterChange} />

        {/* Painel de estatísticas do usuário */}
        {history.length > 0 && <UserStatsPanel history={filteredHistory} />}

        {/* Filtros de visualização */}
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'won', 'lost'].map((filterType) => (
            <button
              key={filterType}
              onClick={() => {
                setFilter(filterType);
                setCurrentPage(1); // Resetar para primeira página
              }}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === filterType
                ? 'bg-brand text-white'
                : 'bg-brand/20 text-gray-400 hover:bg-brand/30'
                }`}
            >
              {filterType === 'all' && `All (${filteredEntries})`}
              {filterType === 'pending' && `Pending Claims (${filteredHistory.filter(e => e.won && !e.claimed).length})`}
              {filterType === 'won' && `Won (${filteredHistory.filter(e => e.won).length})`}
              {filterType === 'lost' && `Lost (${filteredHistory.filter(e => !e.won).length})`}
            </button>
          ))}
        </div>

        {/* Controles de paginação */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">{t('bvb.itemsPerPage')}</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="bg-[#1a1a1a] border border-brand/30 rounded px-2 py-1 text-sm text-white"
            >
              {[5, 10, 20, 50].map(value => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          <span className="text-sm text-gray-400">
            {t('bvb.showingRounds', { shown: paginatedHistory.length, total: processedEntries })}
            {loadingMore && <span className="ml-2 text-brand">{t('bvb.loadingMore')}</span>}
          </span>
        </div>
      </div>

      {/* Conteúdo com scroll */}
       <div className="flex-1 overflow-y-auto p-2" 
      style={{
        minHeight: 0,
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '80px' 
      }}>
        {processedHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-2">
            <FaClock className="text-4xl text-gray-600" />
            <p className="text-gray-400">
              {filter === 'all' ? t('bvb.noHistory') : t('bvb.noFilteredRounds')}
            </p>
          </div>
        ) : (
          <div className="space-y-4 pb-4">
            {paginatedHistory.map((entry, index) => {
              const isPendingClaim = entry.won && !entry.claimed;
              const isRecent = index < 3; // Marcar os 3 mais recentes

              return (
                <div
                  key={`history-${entry.epoch}-${index}`}
                  className={`bg-[#1a1a1a] rounded-lg p-4 border transition-all duration-200 ${isPendingClaim
                    ? 'border-yellow-500/50 bg-yellow-500/5 ring-1 ring-yellow-500/20'
                    : isRecent
                      ? 'border-brand/40 hover:border-brand/60'
                      : 'border-brand/20 hover:border-brand/40'
                    }`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <span className="bg-brand/20 px-2 py-1 rounded text-sm text-gray-300">
                        #{entry.epoch}
                      </span>

                      {isPendingClaim && (
                        <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                          <FaExclamationTriangle size={10} />
                          {t('bvb.claimPending')}
                        </span>
                      )}

                      {isRecent && !isPendingClaim && (
                        <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs font-medium">
                          {t('bvb.recent')}
                        </span>
                      )}

                      <span className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded ${entry.won
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                        }`}>
                        {entry.won ? (
                          <>
                            <FaCheckCircle size={12} />
                            Won
                          </>
                        ) : (
                          t('bvb.lost')
                        )}
                      </span>
                    </div>

                    {entry.timestamp && (
                      <span className="text-xs text-gray-500">
                        {new Date(entry.timestamp).toLocaleString()}
                      </span>
                    )}
                  </div>

                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">{t('bvb.position')}</p>
                      <div className={`flex items-center gap-1 font-medium ${entry.position === 'up' ? 'text-green-500' : 'text-red-500'
                        }`}>
                        {entry.position === 'up' ? (
                          <>
                            <FaArrowUp />
                            {t('bvb.up')}
                          </>
                        ) : (
                          <>
                            <FaArrowDown />
                            {t('bvb.down')}
                          </>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-gray-400 mb-1">{t('bvb.amountBet')}</p>
                      <p className="font-medium text-white">{entry.amount} USDT</p>
                    </div>
                  </div>

                  {entry.won && (
                    <div className="mt-3 pt-3 border-t border-gray-800">
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-400">{t('bvb.winnings')}</p>
                        <p className="font-medium text-green-500">{entry.payout} USDT</p>
                      </div>

                      <div className="flex justify-between items-center mt-2">
                        <p className="text-sm text-gray-400">{t('bvb.status')}</p>
                        {entry.claimed ? (
                          <p className="text-sm text-green-500 flex items-center gap-1">
                            <FaCheckCircle size={12} />
                            {t('bvb.claimed')}
                          </p>
                        ) : (
                          <button
                            onClick={() => handleClaim(entry.epoch)}
                            className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded transition-colors"
                          >
                            {t('bvb.claimWinnings')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Paginação */}
            <HistoryPagination
              totalItems={processedHistory.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />

            {/* Botão para carregar mais histórico */}
            {contractHistory.length > 0 && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={loadMoreHistory}
                  disabled={loadingMore}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${loadingMore
                    ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                    : 'bg-brand/20 text-brand hover:bg-brand/30'
                    }`}
                >
                  {loadingMore ? (
                    <>
                      <FaSync className="inline-block mr-2 spin" size={12} />
                      {t('bvb.loadingOlder')}
                    </>
                  ) : (
                    t('bvb.loadMoreHistory')
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
